import { useTranslation } from "react-i18next";
import { alpha } from "../../theme/tokens";
import { useDesignTokens } from "../../context/ThemeContext";
import { NOZZLE_SECTION_KEY } from "./budgetFormConfig";
import {
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Box,
    Paper,
    Stack,
    IconButton,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
} from "@mui/material";
import React, { useMemo } from "react";
import { Delete, WarningAmber } from "@mui/icons-material";
import NozzleListEditor from "./NozzleListEditor";


const FieldRenderer = ({ field, value, onChange, error, disabled }) => {
    const { t } = useTranslation();
    const commonTextFieldSx = {
        mb: "2px",
        "& .MuiInputBase-root": {
            height: 50,
        },
        "& label.Mui-focused": { color: "primary.main" },
        "& .MuiOutlinedInput-root": {
            "& fieldset": { borderColor: "#ccc" },
            "&:hover fieldset": { borderColor: "#999" },
            "&.Mui-focused fieldset": { borderColor: "primary.main" },
        },
    };

    const commonSelectSx = {
        mb: "2px",
        "& .MuiInputLabel-shrink": { color: "primary.main" },
        "& .MuiInputLabel-root.Mui-focused": { color: "primary.main" },
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "primary.main",
        },
        "& .MuiOutlinedInput-root": {
            height: 50,

        },
    };

    switch (field.type) {
        case "select":
            return (
                <FormControl fullWidth required={field.required} sx={commonSelectSx}>
                    <InputLabel required={field.required}>{t(field.label)}</InputLabel>
                    <Select
                        value={value || ""}
                        label={t(field.label)}
                        required={field.required}
                        onChange={(e) => onChange(field.name, e.target.value)}
                    >
                        {field.options?.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                                {opt}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );

        case "file":
            return (
                <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        {t(field.label)}{field.required && <span className="required-asterisk"> *</span>}
                    </Typography>
                    {!value ? (
                        <Button
                            variant="outlined"
                            component="label"
                            fullWidth
                            sx={{ backgroundColor: "primary.main", color: "white" }}
                        >
                            {t("budgetForm.dataInput.chooseFile")}
                            <input
                                type="file"
                                hidden
                                onChange={(e) => onChange(field.name, e.target.files[0] || null)}
                            />
                        </Button>
                    ) : (
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography
                                variant="body2"
                                sx={{
                                    bgcolor: "#f5f5f5",
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 1,
                                    flex: 1,
                                }}
                            >
                                {value?.name}
                            </Typography>
                            <IconButton
                                color="error"
                                onClick={() => onChange(field.name, null)}
                            >
                                <Delete />
                            </IconButton>
                        </Stack>
                    )}
                </Box>
            );

        default:
            return (
                <TextField
                    fullWidth
                    type={field.type}
                    label={t(field.label)}
                    required={field.required}
                    value={value !== undefined && value !== null ? value : ""}
                    onChange={(e) => onChange(field.name, e.target.value)}
                    helperText={error || ""}
                    error={Boolean(error)}
                    sx={commonTextFieldSx}
                    disabled={disabled}
                />
            );
    }
};


const TabProgressRing = ({ filled, total }) => {
    const tokens = useDesignTokens();
    const pct = total > 0 ? (filled / total) * 100 : 100;

    return (
        <Box sx={{ position: "relative", display: "inline-flex", mr: 1 }}>
            <CircularProgress
                variant="determinate"
                value={100}
                size={20}
                thickness={7}
                sx={{ color: tokens.borderLight }}
            />
            <CircularProgress
                variant="determinate"
                value={pct}
                size={20}
                thickness={7}
                sx={{ color: tokens.primary, position: "absolute", left: 0 }}
            />
        </Box>
    );
};

const getSectionProgress = (section, formData) => {
    let filled = 0;
    const total = section.fields.length;
    section.fields.forEach((field) => {
        const value = formData[field.name];
        if (value !== null && value !== "" && value !== undefined) {
            filled++;
        }
    });
    return { filled, total };
};

export const PreviewSummary = React.memo(({ sections, formData }) => {
    const { t } = useTranslation();
    const { filled, total } = useMemo(() => {
        let filled = 0;
        let total = 0;
        sections.forEach((section) => {
            if (section.title === NOZZLE_SECTION_KEY) return;
            section.fields.forEach((field) => {
                total++;
                const value = formData[field.name];
                if (value !== null && value !== "" && value !== undefined) {
                    filled++;
                }
            });
        });
        return { filled, total };
    }, [sections, formData]);

    return (
        <Paper sx={{ mt: 2, p: 2, bgcolor: alpha.primary08, color: "var(--text-primary)" }}>
            <Typography variant="body2" sx={{ mb: 0.5, color: "var(--text-primary)" }}>
                <span style={{ fontWeight: 600 }}>
                    {t("budgetForm.dataInput.summaryLabel")}
                </span>{" "}
                {t("budgetForm.dataInput.fieldsCompleted")} ({filled} / {total})
            </Typography>
            <Typography variant="body2" sx={{ color: "var(--text-primary)" }}>

            </Typography>
        </Paper>
    );
});



export const PreviewTable = ({ sections, formData }) => {
    const tokens = useDesignTokens();
    const { t } = useTranslation();

    return (
        <TableContainer
            component={Paper}
            sx={{
                border: 1,
                borderColor: "#e0e0e0",
                maxHeight: 570,
                overflow: "auto",
            }}
        >
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: "bold", backgroundColor: tokens.stickyHeaderBg, color: tokens.textPrimary }}>
                            {t("budgetForm.dataInput.fieldColumn")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", backgroundColor: tokens.stickyHeaderBg, color: tokens.textPrimary }}>
                            {t("budgetForm.dataInput.valueColumn")}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sections.map((section) => (
                        <React.Fragment key={section.title}>
                            {section.title !== NOZZLE_SECTION_KEY && (
                                <>
                                    <TableRow sx={{ backgroundColor: alpha.primary08 }}>
                                        <TableCell sx={{ fontWeight: "bold", color: "var(--text-primary)" }} colSpan={2}>
                                            {t(section.title)}
                                        </TableCell>
                                    </TableRow>
                                    {section.fields.map((field) => {
                                        const value = formData[field.name];
                                        let displayValue = value;
                                        if (field.type === "file" && value) {
                                            displayValue = value.name;
                                        } else if (!value) {
                                            displayValue = "-";
                                        }
                                        return (
                                            <TableRow key={field.name}>
                                                <TableCell>{t(field.label)}</TableCell>
                                                <TableCell>{displayValue}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </>
                            )}
                        </React.Fragment>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const DataInputStep = ({
    sections,
    activeTab,
    handleTabChange,
    formData,
    handleChange,
    errors
}) => {
    const tokens = useDesignTokens();
    const { t } = useTranslation();

    const minTemp = Number(formData?.minTemperature);
    const showMinTempWarning =
        !isNaN(minTemp) && minTemp < 0;

    const sectionsProgress = useMemo(
        () => sections.map((section) => getSectionProgress(section, formData)),
        [sections, formData]
    );

    return (
        <Box sx={{ display: "flex", gap: 3, flexDirection: { xs: "column", md: "row" } }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        borderBottom: 1,
                        borderColor: "primary.main",
                        mb: 3,
                        "& .MuiTabs-indicator": {
                            backgroundColor: "primary.main",
                        },
                    }}
                >
                    {sections.map((section, i) => (
                        <Tab
                            key={`tab-${i}`}
                            label={
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <TabProgressRing {...sectionsProgress[i]} />
                                    {t(section.title)}
                                </Box>
                            }
                            sx={{
                                "&.Mui-selected": {
                                    color: "primary.main",
                                    fontWeight: "bold",
                                },
                            }}
                        />
                    ))}
                </Tabs>

                {sections.map((section, i) => (
                    <Box
                        key={`section-${i}`}
                        role="tabpanel"
                        hidden={activeTab !== i}
                    >

                        <Box
                            sx={
                                section.fields.length > 12
                                    ? {
                                        display: "grid",
                                        gridTemplateColumns: "repeat(2, 1fr)",
                                        gap: 2,
                                    }
                                    : {}
                            }
                        >
                            {section.title === NOZZLE_SECTION_KEY ? (
                                <Box sx={{ width: "100%" }}>
                                    <NozzleListEditor
                                        value={formData.nozzleList}
                                        onChange={handleChange}
                                    />
                                </Box>
                            ) : section.fields.length > 12 ? (
                                section.fields.map((field) => (
                                    <Box key={field.name}>
                                        <FieldRenderer
                                            field={field}
                                            value={formData[field.name]}
                                            onChange={handleChange}
                                            error={errors[field.name]}

                                        />
                                    </Box>
                                ))
                            ) : (
                                <Stack spacing={2}>
                                    {section.fields.map((field) => (
                                        <FieldRenderer
                                            key={field.name}
                                            field={field}
                                            value={formData[field.name]}
                                            onChange={handleChange}
                                            error={errors[field.name]}
                                        />
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    </Box>
                ))}

                {showMinTempWarning && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 3,
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: tokens.warningSoft,
                            color: tokens.warning,
                            fontSize: "0.9rem",
                        }}
                    >
                        <WarningAmber fontSize="small" />
                        <span> {t("budgetForm.dataInput.minTempWarning")}</span>
                    </Box>
                )}
            </Box>

            <Box sx={{ display: { xs: "none", md: "block" }, width: 400, flexShrink: 0 }}>
                <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
                    {t("budgetForm.dataInput.preview")}
                </Typography>
                <PreviewTable sections={sections} formData={formData} />
                <PreviewSummary sections={sections} formData={formData} />
            </Box>
        </Box>
    );
};

export default DataInputStep;
